import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { DailyLetter, CoupleSettings, DailyLetterViewRecord } from '../types';
import {
  Mail,
  Heart,
  Sparkles,
  Calendar,
  Star,
  Flame,
  Lock,
  Clock,
  Shield,
  Eye,
  User,
  KeyRound,
  Unlock,
  ArrowLeft,
  Globe,
  Layers,
  Check,
} from 'lucide-react';
import { formatDateSpanish, formatDateTimeSpanish } from '../utils/storage';
import { isLetterSealed } from '../utils/letterLockUtils';
import { DailyLetterModal } from './DailyLetterModal';
import { PrivateLettersPinModal } from './PrivateLettersPinModal';
import { LetterAuditModal } from './LetterAuditModal';
import { romanticAudio } from '../utils/audio';
import { recordDailyLetterOpenInDb, toggleDailyLetterPrivacyInDb } from '../services/firebaseDb';
import { resolveLetterViewer } from '../utils/letterAudit';

interface DailyLettersViewProps {
  letters: DailyLetter[];
  settings: CoupleSettings;
  isAdmin?: boolean;
  onOpenAdmin?: () => void;
  onSaveLetters?: (letters: DailyLetter[]) => void;
  onUpdateLetters?: (letters: DailyLetter[]) => void;
  onGoToGarden?: () => void;
  currentVisitor?: any;
}

export const DailyLettersView: React.FC<DailyLettersViewProps> = ({
  letters,
  settings,
  isAdmin = false,
  onOpenAdmin,
  onSaveLetters,
  onUpdateLetters,
  currentVisitor,
}) => {
  const [selectedLetter, setSelectedLetter] = useState<DailyLetter | null>(null);
  const [selectedAuditLetter, setSelectedAuditLetter] = useState<DailyLetter | null>(null);
  const [viewMode, setViewMode] = useState<'public' | 'private'>('public');
  const [isPrivateUnlocked, setIsPrivateUnlocked] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);

  const p2 = settings.partner2Name || 'Laura';

  // Today's date in YYYY-MM-DD
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Split public and private letters
  const publicLetters = useMemo(() => letters.filter((l) => !l.isPrivate), [letters]);
  const privateLetters = useMemo(() => letters.filter((l) => Boolean(l.isPrivate)), [letters]);

  // Current active set of letters: cartas públicas por defecto, privadas al desbloquear
  const activeLettersPool = viewMode === 'private' ? privateLetters : publicLetters;

  // Check if there is a letter for today in the current pool
  const todayLetter = useMemo(() => {
    return (
      activeLettersPool.find((l) => (l.unlockDate || l.date) === todayStr && !isLetterSealed(l)) ||
      activeLettersPool.find((l) => (l.unlockDate || l.date) === todayStr) ||
      activeLettersPool.find((l) => !isLetterSealed(l)) ||
      activeLettersPool[0] ||
      null
    );
  }, [activeLettersPool, todayStr]);

  // Display all letters in active pool
  const filteredLetters = activeLettersPool;

  const handleToggleFavorite = (letterId: string) => {
    const updated = letters.map((l) =>
      l.id === letterId ? { ...l, isFavorite: !l.isFavorite } : l
    );
    if (onUpdateLetters) {
      onUpdateLetters(updated);
    } else if (onSaveLetters) {
      onSaveLetters(updated);
    }
  };

  const handleRecordLetterOpen = (
    letter: DailyLetter,
    viewerName?: string,
    deviceModel?: string,
    customRecord?: Partial<DailyLetterViewRecord>
  ) => {
    const resolved = resolveLetterViewer(currentVisitor, settings, letter);
    const nowIso = new Date().toISOString();
    const viewRecord: DailyLetterViewRecord = {
      ...resolved,
      openedAt: nowIso,
      ...(viewerName ? { viewerName } : {}),
      ...(deviceModel ? { deviceModel } : {}),
      ...(customRecord || {}),
    };

    recordDailyLetterOpenInDb(letter.id, viewRecord);

    const existingViews = letter.views || [];
    const updatedLetters = letters.map((l) => {
      if (l.id === letter.id) {
        return {
          ...l,
          isRead: true,
          firstOpenedAt: l.firstOpenedAt || nowIso,
          lastOpenedAt: nowIso,
          openedBy: viewRecord.viewerName,
          views: [viewRecord, ...existingViews.filter((v) => v.openedAt !== nowIso)].slice(0, 50),
        };
      }
      return l;
    });

    if (onUpdateLetters) {
      onUpdateLetters(updatedLetters);
    } else if (onSaveLetters) {
      onSaveLetters(updatedLetters);
    }
  };

  const handleOpenLetter = (letter: DailyLetter) => {
    setSelectedLetter(letter);
    romanticAudio.playNote(440, 0.3, 'sine', 0.05);

    if (!isLetterSealed(letter)) {
      const resolved = resolveLetterViewer(currentVisitor, settings, letter);
      handleRecordLetterOpen(letter, resolved.viewerName, resolved.deviceModel, resolved);
    }
  };

  const handleTogglePrivacy = (letterId: string) => {
    let nextState = false;
    const updated = letters.map((l) => {
      if (l.id === letterId) {
        nextState = !l.isPrivate;
        return { ...l, isPrivate: nextState };
      }
      return l;
    });

    if (onUpdateLetters) {
      onUpdateLetters(updated);
    } else if (onSaveLetters) {
      onSaveLetters(updated);
    }

    toggleDailyLetterPrivacyInDb(letterId, nextState).catch((err) => {
      console.warn('Error syncing letter privacy to Firestore:', err);
    });

    if (selectedAuditLetter && selectedAuditLetter.id === letterId) {
      setSelectedAuditLetter({ ...selectedAuditLetter, isPrivate: nextState });
    }
  };

  const handleOpenPrivateVaultClick = () => {
    if (isAdmin || isPrivateUnlocked) {
      setViewMode(viewMode === 'private' ? 'public' : 'private');
      romanticAudio.playNote(520, 0.2, 'sine', 0.05);
    } else {
      setIsPinModalOpen(true);
      romanticAudio.playNote(380, 0.2, 'sine', 0.05);
    }
  };

  const handlePinSuccess = () => {
    setIsPrivateUnlocked(true);
    setViewMode('private');
    romanticAudio.playBloomSuccess();
  };

  const handleLockPrivateVault = () => {
    setIsPrivateUnlocked(false);
    setViewMode('public');
    romanticAudio.playNote(300, 0.3, 'sine', 0.05);
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-6 pb-20 touch-pan-y text-stone-900"
      id="letters-view-container"
    >
      {/* ======================================================== */}
      {/* 1. HERO EXHIBITION BANNER — MAISON DES LETTRES */}
      {/* ======================================================== */}
      <div
        className={`relative rounded-[2.5rem] overflow-hidden p-6 sm:p-10 mb-8 border transition-colors duration-500 shadow-[0_12px_36px_rgba(232,93,142,0.08)] ${
          viewMode === 'private'
            ? 'border-purple-200 bg-gradient-to-br from-white via-[#F8F5FC] to-[#F2ECFA]'
            : 'border-[#F0DDE4] bg-white'
        }`}
      >
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            {/* Minimal Badge / Switcher */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {viewMode === 'public' ? (
                <button
                  type="button"
                  onClick={handleOpenPrivateVaultClick}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] text-[11px] font-bold tracking-wider uppercase shadow-2xs hover:bg-[#FFE8F0] transition-all cursor-pointer"
                  id="cartas-badge"
                  title="Haz clic para ingresar PIN y acceder a cartas privadas"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#E83E78] animate-pulse" />
                  <span>Cartas con Cera</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLockPrivateVault}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-[11px] font-bold tracking-wider uppercase shadow-2xs hover:bg-purple-100 transition-all cursor-pointer"
                  title="Bloquear y volver a cartas públicas"
                  id="btn-back-from-vault"
                >
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  <span>Cartas Privadas</span>
                  <span className="ml-1 px-1.5 py-0.5 rounded-md bg-purple-100 text-[10px] text-purple-700 font-mono">
                    🔓 Volver a públicas
                  </span>
                </button>
              )}
            </div>

            {/* Display Headline */}
            <h1 className="font-['Playfair_Display',serif] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#302B30] tracking-tight leading-tight">
              Cartas dedicadas
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base text-[#77717A] font-medium mt-3 leading-relaxed max-w-xl">
              Palabras sinceras selladas en el tiempo, notas manuscritas y sentimientos dedicados a ti.
            </p>
          </div>

          {/* Action Button: Carta del Día */}
          {todayLetter && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => handleOpenLetter(todayLetter)}
                className="px-5 py-3 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs sm:text-sm shadow-md shadow-pink-500/25 flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap self-stretch sm:self-auto"
                id="btn-open-today-letter"
              >
                {isLetterSealed(todayLetter) ? (
                  <>
                    <Lock className="w-4 h-4 text-white" />
                    <span>Cápsula Sellada 🔒</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 fill-white text-white" />
                    <span>Abrir Carta del Día 💌</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ======================================================== */}
      {/* 2. LETTERS CARDS GRID — VINTAGE LUXURY STATIONERY */}
      {/* ======================================================== */}
      {filteredLetters.length === 0 ? (
        <div className="text-center py-20 px-6 bg-white rounded-3xl border border-[#F0DDE4] flex flex-col items-center max-w-lg mx-auto shadow-2xs">
          <div className="w-16 h-16 bg-[#FFF4F7] border border-[#F0DDE4] rounded-2xl flex items-center justify-center text-[#E83E78] mb-4 shadow-2xs">
            <Mail className="w-8 h-8" />
          </div>
          <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-[#302B30]">
            {viewMode === 'private' ? 'Aún no hay cartas privadas' : 'Aún no hay cartas publicadas'}
          </h3>
          <p className="text-xs sm:text-sm text-[#77717A] mt-2 leading-relaxed max-w-sm">
            {viewMode === 'private'
              ? 'Las cartas íntimas y privadas aparecerán aquí.'
              : 'Las cartas y dedicatorias de amor aparecerán aquí para ser leídas.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 items-stretch">
          {filteredLetters.map((letter) => {
            const isLocked = isLetterSealed(letter);
            const isToday = (letter.unlockDate || letter.date) === todayStr;

            return (
              <motion.div
                key={letter.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="group relative rounded-3xl bg-white border border-[#F0DDE4] shadow-[0_8px_30px_rgba(232,93,142,0.06)] hover:shadow-[0_16px_40px_rgba(232,93,142,0.12)] hover:border-[#E85D8E] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between overflow-hidden"
              >
                {/* Top Subtle Color Band */}
                <div
                  className={`h-1.5 w-full ${
                    isLocked
                      ? 'bg-gradient-to-r from-amber-400 to-[#E85D8E]'
                      : letter.isFavorite
                      ? 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78]'
                      : 'bg-gradient-to-r from-[#FFF4F7] via-[#F0DDE4] to-[#FFF4F7]'
                  }`}
                />

                <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                  {/* Card Header: Wax Seal Badge, Date & Favorite */}
                  <div className="flex items-start justify-between gap-3">
                    {/* Embossed Wax Seal Badge */}
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-md transition-transform group-hover:scale-105 ${
                          isLocked
                            ? 'bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/20'
                            : 'bg-gradient-to-br from-[#E85D8E] to-[#E83E78] shadow-pink-500/25'
                        }`}
                      >
                        {isLocked ? (
                          <Lock className="w-5 h-5 text-white" />
                        ) : (
                          <Heart className="w-5 h-5 fill-white text-white" />
                        )}
                      </div>

                      <div>
                        <div className="text-[11px] font-mono uppercase tracking-wider text-[#77717A] font-semibold flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-[#77717A]" />
                          <span>{formatDateSpanish(letter.unlockDate || letter.date)}</span>
                        </div>
                        {isToday && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#E83E78] bg-[#FFF4F7] border border-[#F0DDE4] px-2 py-0.5 rounded-md mt-0.5">
                            <Sparkles className="w-2.5 h-2.5" />
                            <span>Carta de Hoy</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action icons (Audit and Favorite) */}
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setSelectedAuditLetter(letter)}
                          className="p-1.5 rounded-xl text-[#77717A] hover:text-[#302B30] hover:bg-[#FFF4F7] transition-colors cursor-pointer"
                          title="Auditoría de Lectura y Privacidad"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => handleToggleFavorite(letter.id)}
                        className="p-1.5 rounded-xl text-[#77717A] hover:text-amber-500 hover:bg-amber-50 transition-colors cursor-pointer"
                        title={letter.isFavorite ? 'Quitar de favoritas' : 'Guardar en favoritas'}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            letter.isFavorite ? 'fill-amber-400 text-amber-400' : 'text-[#77717A]/40'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Title & Preview Excerpt */}
                  <div className="space-y-2 flex-1">
                    <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-[#302B30] group-hover:text-[#E83E78] transition-colors line-clamp-1">
                      {letter.title}
                    </h3>

                    {letter.subtitle && (
                      <p className="text-xs font-semibold text-[#77717A] line-clamp-1 italic">
                        "{letter.subtitle}"
                      </p>
                    )}

                    {/* Letter snippet or lock alert */}
                    {isLocked ? (
                      <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-2">
                        <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="font-medium">
                          Sellada hasta el {formatDateSpanish(letter.unlockDate || letter.date)}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs sm:text-[13px] text-[#77717A] line-clamp-3 leading-relaxed font-normal">
                        {letter.letterContent?.bodyParagraphs?.[0] || 'Un mensaje especial aguarda dentro del sobre...'}
                      </p>
                    )}
                  </div>

                  {/* Reading Status & Recipient metadata */}
                  <div className="pt-3 border-t border-[#F0DDE4] flex items-center justify-between text-[11px] text-[#77717A]">
                    <div className="flex items-center gap-1.5 font-medium truncate">
                      <span className="text-[#77717A]">Para:</span>
                      <strong className="text-[#302B30] font-bold">
                        {letter.letterContent?.recipientName || p2}
                      </strong>
                    </div>

                    {letter.isRead ? (
                      <div className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold shrink-0">
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span>Leída</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold shrink-0">
                        <Mail className="w-3 h-3 text-amber-600" />
                        <span>Sin abrir</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenLetter(letter)}
                    className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs ${
                      isLocked
                        ? 'bg-[#FFF4F7] hover:bg-[#FFE8F0] border border-[#F0DDE4] text-[#77717A]'
                        : 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white shadow-md shadow-pink-500/20'
                    }`}
                  >
                    {isLocked ? (
                      <>
                        <Lock className="w-3.5 h-3.5" />
                        <span>Sobre Sellado 🔒</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-3.5 h-3.5" />
                        <span>Abrir Carta ✉️</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Daily Letter Reading Modal */}
      {selectedLetter && (
        <DailyLetterModal
          letter={selectedLetter}
          settings={settings}
          isAdmin={isAdmin}
          onClose={() => setSelectedLetter(null)}
          onToggleFavorite={handleToggleFavorite}
          onRecordOpen={handleRecordLetterOpen}
          currentVisitor={currentVisitor}
        />
      )}

      {/* Private Letters PIN Security Modal */}
      <PrivateLettersPinModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        onSuccess={handlePinSuccess}
      />

      {/* Reading Audit Inspection Modal */}
      <LetterAuditModal
        letter={selectedAuditLetter}
        settings={settings}
        isOpen={Boolean(selectedAuditLetter)}
        onClose={() => setSelectedAuditLetter(null)}
        onTogglePrivacy={handleTogglePrivacy}
      />
    </div>
  );
};
